import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaUserGraduate } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import moment from "moment";
import * as XLSX from "xlsx";

import { fetchPrograms } from "../../../redux/program/programActions";

import Table from "../../../components/Table/Table";
import Loader from "../../../components/Loader/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import CardHeader from "../../../components/Card/CardHeader";
import Modal from "../../../components/Modal/Modal";

import StudentFilter from "./StudentFilter";
import { tableConstants } from "./StudentTableConstant";
import { useStudents } from "./hooks/useStudents";

import {
  deleteStudent,
  downloadStudentData,
  updateStudentStatus,
  directUpdateStudents,
} from "../../../services/StudentService";
import { handleApiError } from "../../../utils/errorHandler";
import "./StudentList.css";
import Button from "../../../components/Button/Button";

/* ================= HELPERS ================= */

const exportToExcel = (data, fileName) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, fileName);
};

const filteredExportColumns = [
  { key: "id", label: "ID" },
  { key: "id_number", label: "Admission No" },
  { key: "student_name", label: "Student Name" },
  { key: "class", label: "Class" },
  { key: "father_name", label: "Father Name" },
  { key: "mother_name", label: "Mother Name" },
  { key: "roll_no", label: "Roll No" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "gender", label: "Gender" },
  { key: "email", label: "Email" },
  { key: "dob", label: "Date of Birth" },
  { key: "house_name", label: "House Name" },
  { key: "apaar_id", label: "Apaar ID" },
  { key: "activation_key", label: "Activation Key" },
];

const preferredExportHeaders = [
  "id_number",
  "student_name",
  "class",
  "father_name",
  "mother_name",
];

const getColumnLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

/* ================= COMPONENT ================= */

const StudentList = () => {
  const dispatch = useDispatch();
  const { programs } = useSelector((state) => state.program);

  const permissions = useMemo(
    () => JSON.parse(localStorage.getItem("permissions")) || [],
    [],
  );

  const sessionId = localStorage.getItem("session_id");
  const rollNoRefs = useRef([]);

  const {
    students,
    setStudents,
    filters,
    setFilters,
    page,
    lastPage,
    isStudentLoading,
    isSearching,
    fetchStudents,
    searchStudents,
    resetStudents,
    handlePageChange,
  } = useStudents();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportRows, setExportRows] = useState([]);
  const [exportFileName, setExportFileName] = useState("");
  const [exportColumns, setExportColumns] = useState([]);
  const [selectedExportHeaders, setSelectedExportHeaders] = useState([]);

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    dispatch(fetchPrograms({ selected: 0 }));
    fetchStudents(1);
  }, [dispatch, fetchStudents]);

  /* ================= FILTER ACTIONS ================= */

  const searchHandler = useCallback(() => {
    if (!filters.name && !filters.idNumber && !filters.programId) {
      enqueueSnackbar("Apply at least one filter", {
        variant: "info",
      });
      return;
    }
    searchStudents(1);
  }, [filters, searchStudents]);

  const resetHandler = useCallback(() => {
    setIsEditMode(false);
    resetStudents();
  }, [resetStudents]);

  const toggleEditMode = useCallback(() => {
    if (!filters.programId || !isSearching) {
      enqueueSnackbar("Select class & search to enable edit mode", {
        variant: "info",
      });
      return;
    }
    setIsEditMode((prev) => !prev);
  }, [filters.programId, isSearching]);

  /* ================= INLINE EDIT ================= */

  const changeHandler = (e, id) => {
    const { name, value } = e.target;

    const updatedStudents = students.map((s) => {
      if (s.id === id) {
        return {
          ...s,
          [name]:
            name === "dob"
              ? moment(value, "YYYY-MM-DD").format("DD-MM-YYYY")
              : value,
          is_updated: true,
        };
      }
      return s;
    });

    setStudents(updatedStudents);
  };

  /* ================= BULK UPDATE ================= */

  const updateBulkHandler = async () => {
    try {
      const payload = students
        .filter((s) => s.is_updated)
        .map((s) => ({
          id: s.id,
          id_number: s.id_number,
          first_name: s.first_name,
          last_name: s.last_name,
          roll_no: s.roll_no,
          dob: s.dob,
          phone1: s.phone1,
          apaar_id: s.apaar_id,
          father_name: s.father_name,
          mother_name: s.mother_name,
          house_name: s.house_name,
          program_id: s.programs?.id,
        }));

      if (!payload.length) {
        enqueueSnackbar("No changes detected", {
          variant: "info",
        });
        return;
      }

      const res = await directUpdateStudents({ data: payload });

      if (res.status === 200) {
        enqueueSnackbar(res.data.message.success, {
          variant: "success",
        });
        setIsEditMode(false);
        handlePageChange(1);
      }
    } catch (error) {
      handleApiError(error, "Bulk update failed");
    }
  };

  /* ================= DELETE ================= */

  const deleteHandler = async (id) => {
    try {
      const res = await deleteStudent(id);
      if (res.status === 200) {
        enqueueSnackbar(res.data.message.success, {
          variant: "success",
        });
        handlePageChange(1);
      }
    } catch (error) {
      handleApiError(error, "Delete failed");
    }
  };

  /* ================= STATUS UPDATE ================= */

  const statusHandler = async (id, status) => {
    try {
      const payload = {
        id,
        suspended: status,
        session_id: sessionId,
      };

      const res = await updateStudentStatus(payload);
      if (res.status === 200) {
        enqueueSnackbar(res.data.message.success, {
          variant: "success",
        });
        handlePageChange(page);
      }
    } catch (error) {
      handleApiError(error, "Status update failed");
    }
  };

  /* ================= DOWNLOAD ================= */

  const openExportSelector = (data, fileName, columns) => {
    const availableKeys = Object.keys(data[0] || {});
    const availableColumns = columns || [
      ...preferredExportHeaders.filter((key) => availableKeys.includes(key)),
      ...availableKeys.filter((key) => !preferredExportHeaders.includes(key)),
    ].map((key) => ({
      key,
      label: getColumnLabel(key),
    }));

    setExportRows(data);
    setExportFileName(fileName);
    setExportColumns(availableColumns);
    setSelectedExportHeaders(availableColumns.map(({ key }) => key));
  };

  const confirmExport = () => {
    if (!selectedExportHeaders.length) {
      enqueueSnackbar("Select at least one header", { variant: "info" });
      return;
    }

    const selectedRows = exportRows.map((row) =>
      selectedExportHeaders.reduce((result, key) => {
        result[key] = row[key];
        return result;
      }, {}),
    );

    exportToExcel(selectedRows, exportFileName);
    setExportRows([]);
  };

  const downloadAllStudents = async () => {
    try {
      setIsDownloading(true);
      const res = await downloadStudentData();
      if (res.status === 200) {
        const data = res.data.data.map((student) => ({
          ...student,
          student_name:
            [student.first_name, student.last_name].filter(Boolean).join(" "),
        }));
        openExportSelector(data, "all-students.xlsx");
      }
    } catch (error) {
      handleApiError(error, "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFilteredStudents = () => {
    const data = students.map((s) => ({
      id_number: s.id_number,
      student_name: [s.first_name, s.last_name].filter(Boolean).join(" "),
      class: s.programs?.name || "",
      father_name: s.father_name || "",
      mother_name: s.mother_name || "",
      id: s.id,
      roll_no: s.roll_no,
      first_name: s.first_name,
      last_name: s.last_name,
      gender: s.gender,
      email: s.email,
      dob: s.dob ? moment(s.dob, "DD-MM-YYYY").format("YYYY-MM-DD") : "",
      house_name: s.house_name,
      apaar_id: s.apaar_id,
      activation_key: s.activation_key,
    }));
    openExportSelector(data, "filtered-students.xlsx", filteredExportColumns);
  };

  /* ================= RENDER ================= */

  return (
    <div className="card">
      <CardHeader
        title="Students"
        icon={<FaUserGraduate />}
        button={
          <>
            <Button
              type="submit"
              variant="primary"
              className="mx-2"
              onClick={downloadAllStudents}
            >
              {isDownloading ? "Downloading..." : "All Students Download"}
            </Button>

            {permissions.includes("student.create") && (
              <Link className="btn btn-primary" to="/student/create">
                Add Student
              </Link>
            )}
          </>
        }
      />
      <div className="card-body">
        <StudentFilter
          type="student"
          programList={programs?.data || []}
          filters={filters}
          setFilters={setFilters}
          onSearch={searchHandler}
          onReset={resetHandler}
          isEditMode={isEditMode}
          onToggleEditMode={toggleEditMode}
          isSearching={isSearching}
          onDownload={downloadFilteredStudents}
        />
        {isStudentLoading && <Loader />}

        <div className="mt-3">
          {students.length ? (
            <Table
              cols={tableConstants(
                permissions,
                statusHandler,
                deleteHandler,
                isEditMode,
                changeHandler,
                rollNoRefs.current,
                page,
              )}
              data={students}
              striped
              hoverable
              getRowClassName={(row) =>
                Number(row.suspended) !== 0 ? "student-row-suspended" : ""
              }
            />
          ) : (
            <div className="student-empty-state">
              <FaUserGraduate />
              <span>No data found</span>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={exportRows.length > 0}
        onClose={() => setExportRows([])}
        title="Select download headers"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setExportRows([])}
            >
              Cancel
            </Button>
            <Button variant="primary" type="button" onClick={confirmExport}>
              Download
            </Button>
          </>
        }
      >
        <div className="student-export-intro">
          <div>
            <strong>Choose the columns for your spreadsheet</strong>
            <span>Select only the information you need in the download.</span>
          </div>
          <span className="student-export-count">
            {selectedExportHeaders.length}/{exportColumns.length} selected
          </span>
        </div>
        <label className="student-export-select-all">
          <input
            type="checkbox"
            checked={
              exportColumns.length > 0 &&
              selectedExportHeaders.length === exportColumns.length
            }
            ref={(input) => {
              if (input) {
                input.indeterminate =
                  selectedExportHeaders.length > 0 &&
                  selectedExportHeaders.length < exportColumns.length;
              }
            }}
            onChange={() =>
              setSelectedExportHeaders((current) =>
                current.length === exportColumns.length
                  ? []
                  : exportColumns.map(({ key }) => key),
              )
            }
          />
          <span>
            {selectedExportHeaders.length === exportColumns.length
              ? "Clear all headers"
              : "Select all headers"}
          </span>
        </label>
        <div className="student-export-header-list">
          {exportColumns.map(({ key, label }) => (
            <label className="student-export-header-option" key={key}>
              <input
                type="checkbox"
                checked={selectedExportHeaders.includes(key)}
                onChange={() =>
                  setSelectedExportHeaders((current) =>
                    current.includes(key)
                      ? current.filter((selectedKey) => selectedKey !== key)
                      : [...current, key],
                  )
                }
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </Modal>

      <div className="card-footer">
        {isEditMode && (
          <div className="d-flex justify-content-end mb-2">
            <Button type="submit" variant="primary" onClick={updateBulkHandler}>
              Update
            </Button>
          </div>
        )}

        {!!students.length && (
          <Pagination
            totalPage={lastPage}
            handlePageChange={({ selected }) => handlePageChange(selected + 1)}
          />
        )}
      </div>
    </div>
  );
};

export default StudentList;
